import i18next from 'i18next';
import { setKioskLanguageRequest } from 'api/requests/kiosk-language-requests';
import kioskSessionService from 'services/kiosk-session-service';
import Lang from 'types/Lang';
import { toKioskLanguageId } from 'utils/kiosk-language';

// Only the last choice of a burst of switches is saved (the AC's "several switches within two seconds").
// Safe to be this long: the UI switches instantly, the pending choice is kept in local storage, and every
// kiosk request sends it first via settle().
const SAVE_DEBOUNCE_MS = 1500;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let inFlightSave: Promise<void> | null = null;
let requestedLanguage: Lang | null = null;

/**
 * Switches i18next without flicker. i18next 21 lets a switch whose language is still downloading land
 * after a newer switch and set the older language back, so download first and only apply the latest pick.
 */
const switchTo = async (lang: Lang): Promise<void> => {
  requestedLanguage = lang;
  if (!i18next.hasResourceBundle(lang, 'translation')) {
    try {
      await i18next.loadLanguages(lang);
    } catch {
      // Fall through: i18next shows the fallback text for anything missing.
    }
    if (requestedLanguage !== lang) return;
  }
  if (i18next.language !== lang) {
    await i18next.changeLanguage(lang);
  }
};

const isRetryable = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  // No response = offline / timeout; 5xx = transient. A 4xx (unsupported language, ended session)
  // will never succeed, so drop it rather than retrying forever.
  return !status || status >= 500;
};

const send = async (): Promise<void> => {
  const lang = kioskSessionService.getPendingLanguage();
  const token = kioskSessionService.getToken();
  if (!lang || !token) {
    return;
  }

  try {
    await setKioskLanguageRequest(token, toKioskLanguageId(lang));
    // Only clear if nothing newer was selected while this request was in flight.
    if (kioskSessionService.getPendingLanguage() === lang) {
      kioskSessionService.clearPendingLanguage();
    }
  } catch (error) {
    if (!isRetryable(error)) {
      kioskSessionService.clearPendingLanguage();
    }
    // Retryable: keep it pending; the next settle() or the browser coming back online re-sends it.
  }
};

/**
 * Persists the kiosk's language on its server session. The UI never waits on this: the language is
 * applied locally first, and a failed save stays pending (local storage) until the network is back.
 */
const kioskLanguageService = {
  /** The selector: switch the whole kiosk UI instantly, then save the choice in the background. */
  select: (lang: Lang): void => {
    switchTo(lang);
    kioskSessionService.setPendingLanguage(lang);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      kioskLanguageService.settle();
    }, SAVE_DEBOUNCE_MS);
  },

  /** Apply a language without saving it (activation / resume, where the server already has it). */
  apply: (lang: Lang): void => {
    switchTo(lang);
  },

  /**
   * Send any pending language now and wait for it. Every kiosk request calls this first, so a
   * clock-in made right after a switch reaches the server after the new language does.
   */
  settle: async (): Promise<void> => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (inFlightSave) {
      await inFlightSave;
    }
    if (!kioskSessionService.getPendingLanguage()) {
      return;
    }
    inFlightSave = send().finally(() => {
      inFlightSave = null;
    });
    await inFlightSave;
  },
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    kioskLanguageService.settle();
  });
}

export default kioskLanguageService;
